/**
 * Routing, and the decision about what the user is looking at.
 *
 * The router is where the decision is made, and there are three states rather than two. Signed out and
 * signed in are the two the product has; the third is *not known yet*, and it is not a synonym for
 * signed out. Two kinds of question can be unanswered at once — whether there is a session, and whether
 * this deployment has accounts at all — and a tree rendered from a half-answered pair is a claim the app
 * cannot support: an authentication form shown because `/meta` had not answered is a lie about the
 * deployment, not a conservative default.
 *
 * A screen never navigates on a successful sign-in: the session state changes, this component
 * re-renders, and a different tree appears. Two sources of truth for "where am I" is how a user ends
 * up on a screen with no session after a refresh failed.
 *
 * The `restored` flag is why the window is representable at all: after a reload the access token is gone
 * by design (contract §3.1), so `user === null` means "not known yet" until the refresh attempt answers.
 * Rendering the sign-in screen during that window would flash it at a signed-in user on every reload —
 * and the same argument extends to the mode, which is why it is `unknown` rather than `false` until the
 * server says which it is.
 */

import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { copy } from "./copy.ts";
import { ToastProvider } from "./components/Toast.tsx";
import { SessionProvider, useCurrentUser, usePublicMode, useRestoredSession } from "./lib/session-context.tsx";
import { CaptureScreen } from "./screens/Capture.tsx";
import { HomeScreen } from "./screens/Home.tsx";
import { PlaceDetailScreen } from "./screens/PlaceDetail.tsx";
import { PlacesScreen } from "./screens/Places.tsx";
import { PlannerScreen } from "./screens/Planner.tsx";
import { SettingsScreen } from "./screens/Settings.tsx";
import { SignInScreen } from "./screens/SignIn.tsx";
import { TripDetailScreen } from "./screens/TripDetail.tsx";
import { TripsScreen } from "./screens/Trips.tsx";
import { AppShell, type ShellLayout } from "./shell/AppShell.tsx";

/**
 * Everything an authenticated route renders inside, and how much chrome the shell shows on a phone.
 *
 * **Every one of these routes renders inside `AppShell`**, which is what makes the frame — the rail on a
 * desktop, the tab bar on a phone — a property of being signed in rather than of which screen you are
 * looking at. The single exception is the sign-in screen, and it is not in this table because it is not
 * behind a session: `Shell()` returns it before any route is matched.
 *
 * `overlay` is not "outside the shell" any more. It means **no tab bar on a phone**, for the two screens
 * the spec calls tasks rather than destinations — the capture form and the place card. Measured reason,
 * unchanged: a form with a keyboard open loses a row of fields to a bar it cannot use, and the capture
 * form is 1503px in an 844px viewport. On a desktop the rail is *beside* the content rather than under
 * it, so it costs those screens nothing and is shown like everywhere else (the product owner's rule:
 * every page keeps the left menu except authentication).
 */

interface AppRoute {
  path: string;
  element: ReactElement;
  /**
   * Required, and that requirement is why this table exists rather than JSX.
   *
   * A layout chosen per route, as a literal in a table, makes it unrepresentable for a screen to be
   * rendered without a frame — which is not a hypothetical. The capture form and the place card were once
   * siblings of `AppShell`, so they were outside `.shell__main`, the only scrolling container in the app,
   * while `body` is `overflow: hidden`: the form was 1503px tall in an 844px viewport and **neither a
   * wheel event nor a touch drag moved it**. Everything below the fold, including the save button, was
   * unreachable and nothing on screen said so. Now every route here is inside the shell and scrolls the
   * same way; this field only decides how much chrome the shell shows on a phone.
   */
  layout: ShellLayout;
  /**
   * True for the one route that exists only where accounts do.
   *
   * The account screen is the product's session surface — who is signed in, which devices, and the way
   * out. A deployment that serves everyone as one shared account has none of those, so the route is
   * dropped from the table above and `/profile` falls through to the catch-all, which sends the visitor
   * to Home. It is a property of the route rather than a filter by path, because "this screen is about
   * an account" is what makes it conditional and a string comparison against `/profile` elsewhere would
   * be a second, silent copy of that fact.
   */
  accountsOnly?: true;
}

/**
 * Every route, in one place.
 *
 * Rendered by grouping on `layout`, rather than nested JSX, so the layout is a property of the route
 * rather than of where it happens to sit in the tree. React Router v6 ranks routes by specificity rather
 * than declaration order, so the grouping does not affect matching — `/trips/new` still wins over
 * `/trips/:tripId`.
 */
const APP_ROUTES: readonly AppRoute[] = [
  // Overlays: the spec lists the capture form and the place card as tasks you finish rather than places
  // you navigate to, and both need the whole screen — a form with a keyboard open loses a row of fields
  // to a tab bar it cannot use.
  { path: "/capture", element: <CaptureScreen />, layout: "overlay" },
  { path: "/places/:placeId", element: <PlaceDetailScreen />, layout: "overlay" },

  { path: "/home", element: <HomeScreen />, layout: "shell" },

  { path: "/trips", element: <TripsScreen />, layout: "shell" },
  { path: "/trips/new", element: <PlannerScreen />, layout: "shell" },
  // A saved trip is a place, not a one-shot flow. This is what makes an itinerary survive leaving the
  // screen that generated it — `/trips/new` is a builder, and it is the only route that existed.
  { path: "/trips/:tripId", element: <TripDetailScreen />, layout: "shell" },
  // Editing a trip is the planner, on a trip that already exists. One screen and one set of rules: a second
  // form for the same fields would drift from the planner the first time either changed, and the planner is
  // what owns what a trip *is*.
  { path: "/trips/:tripId/edit", element: <PlannerScreen />, layout: "shell" },

  { path: "/places", element: <PlacesScreen />, layout: "shell" },

  // The settings and account screen, available to everyone across all modes.
  { path: "/settings", element: <SettingsScreen />, layout: "shell" },

  // Redirect legacy /profile to /settings
  { path: "/profile", element: <Navigate to="/settings" replace />, layout: "shell" },

  // Home, not a list. The spec makes Home the answer to "what next?" — and an empty list, which is what
  // a first-run user has, answers nothing.
  { path: "*", element: <Navigate to="/home" replace />, layout: "shell" },
];

/** The signed-in application. */
function SignedInApp(): ReactElement {
  const mode = usePublicMode();
  // The account route is the only one a deployment can be without; where there are no accounts, `/profile`
  // is not a route and the catch-all below answers it with Home. The filter is here rather than in the table
  // so the decision is made from the live mode, which is what `PUBLIC_MODE=false` flips back.
  const routes = APP_ROUTES.filter((route) => !(route.accountsOnly === true && mode === "public"));
  const inLayout = (layout: ShellLayout): ReactElement[] =>
    routes.filter((route) => route.layout === layout).map((route) => (
      <Route key={route.path} path={route.path} element={route.element} />
    ));

  return (
    <Routes>
      <Route element={<AppShell layout="overlay" />}>{inLayout("overlay")}</Route>
      <Route element={<AppShell layout="shell" />}>{inLayout("shell")}</Route>
    </Routes>
  );
}

function Shell(): ReactElement {
  const restored = useRestoredSession();
  const user = useCurrentUser();
  const mode = usePublicMode();

  /*
   * The mode decides, and "not known yet" is one of its three states rather than the negation of public.
   *
   * **Unknown renders neither the app nor the form.** It is not "signed out": `user === null` before the
   * boot settles means "not known yet" — the access token is deliberately absent after a reload — and an
   * unanswered `/meta` means the deployment itself is not known yet. A loading line is what is true in
   * that window; the sign-in screen is a claim that this deployment has accounts, and making that claim
   * from "no answer" is exactly the bug this replaced. A hard load of a non-home path used to paint the
   * form for that reason, and it stuck there.
   *
   * **Only a decided private deployment may show an authentication form.** The boot resolves the shared
   * account from the API with no credentials at all, so in public mode `user` is not null while the API
   * answers; and when it is not readable — a server started before its migrations — the mode is still
   * `public`, so this branch is not reached and the app renders instead. `/sign-in` and `/signup` are not
   * routes in either mode: they are the screen the router falls back to, so a URL nobody should have
   * falls through to the catch-all in `APP_ROUTES` and the visitor lands on Home. With `PUBLIC_MODE=false`
   * the boot finds no session and this is the sign-in screen again, exactly as before.
   */
  if (!restored || mode === "unknown") return <p className="state">{copy.state.loading}</p>;
  if (user === null && mode === "private") return <SignInScreen />;
  return <SignedInApp />;
}

export function App(): ReactElement {
  return (
    <SessionProvider>
      {/*
        Toast sits inside the session provider because a toast reports the outcome of a request, and
        outside the router's screens so a message survives a navigation — a save that reports success
        while the user is already being sent to the list would otherwise unmount its own confirmation.
      */}
      <ToastProvider>
        <Shell />
      </ToastProvider>
    </SessionProvider>
  );
}
